import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import pickBy from 'lodash/pickBy';
import { isProductAvailableForSale, getStockInfo, isBackorderAllowed } from './utils/product.js';
import get from 'lodash/get';
import set from 'lodash/set';
import find from 'lodash/find';
import mergeWith from 'lodash/mergeWith';
import isEqual from 'lodash/isEqual';
import { c as colorCodesList, v as validationSchema, g as getInitialValues } from './index-NdDXdXEk.js';
import Button from './components/Button/index.js';
import InfoVariantModal from './components/InfoVariantModal/index.js';
import { C as Counter } from './Counter-VXuTWVIz.js';
import { C as ColorField } from './ColorField-_HVyME4u.js';
import { S as SizeField } from './SizeField-JNtoYwfb.js';
import { R as RelatedProducts } from './RelatedProducts-9tDufU_z.js';
import { c as classnames } from './index-2586sEvh.js';
import { CoreContext } from './context/index.js';
import './utils/logToSlack.js';
import './animations--PMkOS0j.js';
import 'cookies-next';
import { M as MD_WIDTH } from './breakpoints-X1SKRYyW.js';
import './currency-_kacFJHO.js';
import './language-BHHwgtQp.js';
import './payment-YC9H491Y.js';
import './index-xPaCrlMu.js';
import useShowError from './hooks/useShowError.js';
import useShowNotify from './hooks/useShowNotify.js';
import './helpers-kA_hdoOI.js';
import 'lodash';
import { A as AlmaBanner } from './AlmaBanner-qWjGjPhU.js';
import { g as gsapWithCSS } from './index-RkBJ48Nr.js';

var FieldType;
(function (FieldType) {
    FieldType["Color"] = "Color";
    FieldType["Dropdown"] = "Dropdown";
})(FieldType || (FieldType = {}));

var AttributeValueStatus;
(function (AttributeValueStatus) {
    AttributeValueStatus["Available"] = "available";
    AttributeValueStatus["Disabled"] = "disabled";
    AttributeValueStatus["Unavailable"] = "unavailable";
})(AttributeValueStatus || (AttributeValueStatus = {}));
const getVariantInfo = (variantsInfo, attributeCode) => find(variantsInfo, { code: attributeCode });
const getFieldType = (attributeCode) => {
    if (colorCodesList.includes(attributeCode))
        return FieldType.Color;
    return FieldType.Dropdown;
};
const getFilteredOptionValues = (option, availableAttribute) => {
    const values = (option === null || option === void 0 ? void 0 : option.values) || [];
    return values
        .filter(value => get(availableAttribute, [option.attribute_code, value.label]) !==
        AttributeValueStatus.Unavailable)
        .map(value => ({
        ...value,
        disabled: get(availableAttribute, [option.attribute_code, value.label]) ===
            AttributeValueStatus.Disabled,
    }));
};
const getOption = (option, availableAttribute) => ({
    ...option,
    values: getFilteredOptionValues(option, availableAttribute),
});
// This method checks and updates all appropriate attribute values to "available" where it is needed
// This method mutates passed "calculatedAttribute" object
const filterVariants = (variants, attributeListItem, calculatedAttribute) => {
    const attributeListItemOptions = attributeListItem.options;
    const attributeListOptionCodes = Object.keys(attributeListItemOptions);
    // Iterate through the variants to check for available attribute values
    variants.forEach(variant => {
        const attributes = (variant === null || variant === void 0 ? void 0 : variant.attributes) || [];
        // An object that contains a current variant options
        const variantOptionsMap = attributes.reduce((acc, val) => ({
            ...acc,
            [val.code]: val.label,
        }), {});
        // Remove unnecessary options from VariantOptions
        // Keep only those options which are present within AttributeList options
        // This is required for the correct options comparison
        const filteredVariantOptionsMap = pickBy(variantOptionsMap, (_, key) => attributeListOptionCodes.includes(key));
        // Compare AttributeListItem options with Variant option
        // If the options are match then change the Attribute value to "available"
        if (isEqual(attributeListItemOptions, filteredVariantOptionsMap)) {
            const { attributeCode, attributeValue } = attributeListItem;
            set(calculatedAttribute, [attributeCode, attributeValue], AttributeValueStatus.Available);
        }
    });
};
const prepareAttributeByStatusMask = (attributeList, status) => attributeList.reduce((acc, val) => {
    const attributeCode = {
        ...acc[val.attributeCode],
        [val.attributeValue]: status,
    };
    const tempAcc = {
        ...acc,
        [val.attributeCode]: attributeCode,
    };
    return tempAcc;
}, {});
const getAvailableAttribute = (product, selectedOptions) => {
    const configurableOptions = (product === null || product === void 0 ? void 0 : product.configurable_options) || [];
    const variants = (product === null || product === void 0 ? void 0 : product.variants) || [];
    // A combinations of attribute values with prepared options mask
    const attributeList = [];
    configurableOptions.forEach(configurableOption => {
        const attributeCode = configurableOption.attribute_code;
        const values = configurableOption.values || [];
        values.forEach(value => {
            const attributeValue = value.label;
            const item = {
                attributeCode,
                attributeValue,
                options: { ...selectedOptions, [attributeCode]: attributeValue },
            };
            attributeList.push(item);
        });
    });
    // Available and disabled variants (no unavailable variants)
    const availableAndDisabledVariants = variants;
    // Available variants (no unavailable and disabled variants)
    const availableVariants = variants.filter(variant => isProductAvailableForSale(variant.product.backorder_status, variant.product.stock_status));
    // This attribute object contains a predefined mask with only "unavailable" value statuses
    // This object will be mutated within the "filterVariants" method
    const allAndUnavailableAttribute = prepareAttributeByStatusMask(attributeList, AttributeValueStatus.Unavailable);
    // This attribute object contains a predefined mask with only "disabled" value statuses
    // This object will be mutated within the "filterVariants" method
    const allAndDisabledAttribute = prepareAttributeByStatusMask(attributeList, AttributeValueStatus.Disabled);
    // Iterate through all possible attribute values
    attributeList.forEach(item => {
        filterVariants(availableAndDisabledVariants, item, allAndUnavailableAttribute);
        filterVariants(availableVariants, item, allAndDisabledAttribute);
    });
    // The final proceeded attribute list which contains "available", "disabled" and "unavailable" value statuses
    const calculatedAttribute = mergeWith(allAndUnavailableAttribute, allAndDisabledAttribute, (objValue, srcValue) => {
        const onlyUnavailableAttribute = pickBy(objValue, val => val === AttributeValueStatus.Unavailable);
        return {
            ...srcValue,
            ...onlyUnavailableAttribute,
        };
    });
    return calculatedAttribute;
};
const getOptions = (product, selectedOptions) => {
    const availableAttribute = getAvailableAttribute(product, selectedOptions);
    const configurableOptions = product.configurable_options || [];
    const variantsInfo = get(product, 'variants_info.items', []);
    const options = configurableOptions.map(option => {
        var _a;
        return ({
            option: getOption(option, availableAttribute),
            variantInfoContent: (_a = getVariantInfo(variantsInfo, option.attribute_code)) === null || _a === void 0 ? void 0 : _a.content,
            fieldType: getFieldType(option.attribute_code),
            disabled: false,
        });
    });
    return options;
};
const checkIsFormFilled = (optionsList, values) => optionsList.every(option => values[option]);
const isWholeProductAvailableForSale = (productVariants) => productVariants.some(item => { var _a, _b; return isProductAvailableForSale((_a = item === null || item === void 0 ? void 0 : item.product) === null || _a === void 0 ? void 0 : _a.backorder_status, (_b = item === null || item === void 0 ? void 0 : item.product) === null || _b === void 0 ? void 0 : _b.stock_status); });

const getConfigurableOptionsList = (options) => options.map(option => option.attribute_code);

const CardSelector = ({ option, disabled, control, errors, setCurrentScent, attributeCode, }) => {
    const { t } = useTranslation('', { keyPrefix: '$common.$validation' });
    const [selectedScent, setSelectedScent] = useState('');
    const handleHover = (itemLabel) => {
        if (setCurrentScent) {
            setCurrentScent(itemLabel, attributeCode);
        }
    };
    const handleHoverExit = () => {
        if (setCurrentScent) {
            setCurrentScent(selectedScent, attributeCode);
        }
    };
    return (React.createElement("div", { className: "form-field" },
        React.createElement(Controller, { name: option.attribute_code, control: control, defaultValue: "", render: ({ field: { onChange, value } }) => (React.createElement("div", { className: "card-selector" }, option.values.map(item => {
                const isDisabled = disabled && value !== item.label;
                return (React.createElement("button", { key: item.value_index, type: "button", className: classnames('card-selector-item', {
                        active: value === item.label,
                        disabled: isDisabled,
                    }), onMouseEnter: () => {
                        handleHover(item.label);
                    }, onMouseLeave: () => {
                        handleHoverExit();
                    }, onClick: () => {
                        onChange(item.label);
                        setSelectedScent(item.label);
                        if (setCurrentScent) {
                            setCurrentScent(item.label, attributeCode);
                        }
                    }, disabled: isDisabled }, item.label));
            }))) }),
        errors[option.attribute_code] && (React.createElement("span", { className: "error-message", role: "alert" }, t('selectOption')))));
};

const CardField = ({ option, control, errors, onOptionsChange, setCurrentScent }) => {
    useEffect(() => {
        if (typeof onOptionsChange === 'function')
            onOptionsChange(option.option.attribute_code);
    }, [option.option.values.length]);
    return (React.createElement("div", { className: "col-left" },
        React.createElement(CardSelector, { option: option.option, disabled: option.disabled, control: control, errors: errors, setCurrentScent: setCurrentScent, attributeCode: option.option.attribute_code })));
};

const OptionsForm = ({ product, isFormFilled, setFormValues, setIsFormFilled, onSubmit, loading, selectedRelatedProductsSku, counterValue, setCounterValue, addonCounterValue, setAddonCounterValue, checkedAddon, onRelatedProductClick, onColorChange, selectedProductVariant, isSingleOptionProduct, setIsSingleOptionProduct, submitRef, prices, pageReady = true, }) => {
    var _a, _b, _c, _d;
    const { t } = useTranslation('', { keyPrefix: '' });
    const { t: tLabel } = useTranslation('', { keyPrefix: '$common.$labels' });
    const { t: tValidation } = useTranslation('', { keyPrefix: '$common.$validation' });
    const showErrorNotify = useShowError();
    const { data: { siteConfig: { product: { counterButtons = false, alignCounterWithCartBtn = false, textSizeGuideButton = false, scentKeys = [], } = {}, } = {}, } = {}, } = useContext(CoreContext);
    // Get scents from context or use empty object as fallback
    const scents = ((_d = (_c = (_b = (_a = useContext(CoreContext)) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.siteConfig) === null || _c === void 0 ? void 0 : _c.product) === null || _d === void 0 ? void 0 : _d.scents) || {};
    const [variantInfoContent, setVariantInfoContent] = useState(null);
    const [addOnOutStockNotified, setAddOnaddOnOutStockNotified] = useState(false);
    const [currentScent, setCurrentScent] = useState('');
    const [isTransitioning, setIsTransitioning] = useState(false);
    const transitionTimeoutRef = useRef(null);
    const router = useRouter();
    const showNotify = useShowNotify();
    const infoVariantButtonRef = useRef(null);
    const configurableOptions = product.configurable_options;
    const configurableOptionsList = getConfigurableOptionsList(configurableOptions);
    // If a product options are not selected - show a root related product (for a preview)
    // If selected - show variant product related product
    const relatedProducts = selectedProductVariant
        ? (selectedProductVariant === null || selectedProductVariant === void 0 ? void 0 : selectedProductVariant.related_products) || []
        : product.related_products;
    const { register, control, handleSubmit, watch, reset, formState: { errors }, setFocus, setValue, } = useForm({
        resolver: yupResolver(validationSchema(configurableOptionsList)),
        defaultValues: getInitialValues(configurableOptions),
        mode: 'onSubmit',
        shouldFocusError: false,
    });
    const maxQty = 99;
    const formValues = watch();
    const selectedOptions = pickBy(formValues, (val, key) => configurableOptionsList.includes(key) && Boolean(val));
    const options = getOptions(product, selectedOptions);
    const stockInfo = getStockInfo(selectedProductVariant === null || selectedProductVariant === void 0 ? void 0 : selectedProductVariant.backorder_status, selectedProductVariant === null || selectedProductVariant === void 0 ? void 0 : selectedProductVariant.stock_status, Number(selectedProductVariant === null || selectedProductVariant === void 0 ? void 0 : selectedProductVariant.quantity), t);
    const isProductAvailableForSale = isWholeProductAvailableForSale(product.variants);
    const parentStockStatus = product.stock_status;
    const productStockMessage = useMemo(() => {
        if (parentStockStatus === 'OUT_OF_STOCK')
            return t('$common.$labels.outStock');
        if (!isFormFilled && !isProductAvailableForSale)
            return t('$common.$labels.outStock');
        if (isFormFilled && stockInfo.qty === 0)
            return t('$common.$labels.outStock');
        if (isFormFilled && stockInfo.message)
            return stockInfo.message;
        return '';
    }, [isFormFilled, isProductAvailableForSale, stockInfo.qty, stockInfo.message]);
    const handleClickAttributeInfo = (option) => {
        setVariantInfoContent(option.variantInfoContent);
    };
    useEffect(() => {
        const subscription = watch(values => {
            // We are using here a custom method "checkIsFormFilled" instead of prepared react-hook-form method "isValid"
            // because "isValid" method will not dynamically update on each field change within mode: 'onSubmit'
            // We need to use mode: 'onSubmit' due to ADA Compliance (do not show field(s) error message until form submit)
            const isFilled = checkIsFormFilled(configurableOptionsList, values) || isSingleOptionProduct;
            setIsFormFilled(isFilled);
            setFormValues(values);
        });
        return () => subscription.unsubscribe();
    }, [watch, router.asPath]);
    // Reset form and Counter values on route or locale change
    useEffect(() => {
        reset(getInitialValues(configurableOptions));
        setCounterValue(1);
        setAddonCounterValue(1);
    }, [router.asPath, router.locale]); // Warning: If we add new dependencies here, it will cause the form to reset on every change of these dependencies.
    // Adjusts main and related product quantities based on stock. Shows a notification if selected quantity is reduced.
    // Note: Doesn't account for quantities in the cart.
    const onMaxReached = () => {
        const maxCounterValue = Math.min(maxQty, stockInfo.qty);
        setCounterValue(maxCounterValue);
        setAddonCounterValue(maxCounterValue);
        if (maxCounterValue === maxQty) {
            showErrorNotify(t('$common.$validation.maximumQuantity'));
        }
        else {
            showNotify(tValidation('outStockReduceQtyForName', { productName: product.name }));
        }
    };
    useEffect(() => {
        if (selectedProductVariant) {
            setAddOnaddOnOutStockNotified(false);
        }
    }, [selectedProductVariant]);
    useEffect(() => {
        const selected = relatedProducts
            .filter(item => selectedRelatedProductsSku.includes(item.sku))
            .sort((a, b) => a.quantity - b.quantity)[0];
        const mainOutStock = counterValue > stockInfo.qty && stockInfo.qty > 0;
        const addOnOutStock = (selected === null || selected === void 0 ? void 0 : selected.quantity) < counterValue;
        // If Configurable product options were not selected
        // then set the related product addonCounterValue to be the same as main Config product counterValue
        if (!isFormFilled) {
            setAddonCounterValue(counterValue);
        }
        if (stockInfo.availableForSale &&
            !isBackorderAllowed(selectedProductVariant === null || selectedProductVariant === void 0 ? void 0 : selectedProductVariant.backorder_status)) {
            // If both the main and related product are available in the requested quantity,
            // then set the addonCounterValue to the counterValue
            if (!mainOutStock && !addOnOutStock) {
                setAddonCounterValue(counterValue);
            }
            if (mainOutStock || counterValue > maxQty) {
                onMaxReached();
            }
            if (addOnOutStock && (selected === null || selected === void 0 ? void 0 : selected.quantity) !== addonCounterValue) {
                setAddOnaddOnOutStockNotified(true);
                setAddonCounterValue(selected === null || selected === void 0 ? void 0 : selected.quantity);
                showNotify(tValidation('outStockReduceQtyForName', { productName: selected === null || selected === void 0 ? void 0 : selected.name }));
            }
            else if (addOnOutStock && !addOnOutStockNotified) {
                showNotify(tValidation('outStockReduceQtyForName', { productName: selected === null || selected === void 0 ? void 0 : selected.name }));
                setAddOnaddOnOutStockNotified(true);
            }
        }
    }, [counterValue, stockInfo]);
    // This is the fix for the issue when the dropdown options length is changing and it shows the incorrect option value
    // This issue is caused by the requirement to dynamically hide the unavailable product options from the dropdown list
    const onOptionsChange = (fieldName) => {
        setValue(fieldName, formValues[fieldName]);
    };
    const onInfoVariantModalClose = () => {
        setVariantInfoContent(null);
        if (infoVariantButtonRef.current) {
            infoVariantButtonRef.current.focus();
        }
    };
    // Automatically select the first option if options length is 1 and values lenght is 1
    const oneOptionWithOneValue = options.length === 1 && options[0].option.values.length === 1;
    useEffect(() => {
        if (oneOptionWithOneValue) {
            setIsSingleOptionProduct(true);
            const firsOption = options[0];
            if (firsOption && firsOption.option.values && firsOption.option.values[0]) {
                setValue(firsOption.option.attribute_code, firsOption.option.values[0].label);
                setIsFormFilled(true);
            }
        }
    }, [router, isFormFilled, isSingleOptionProduct, router.asPath]);
    // Check if any option contains variantInfoContent
    const hasVariantInfoContent = options.some(option => option.variantInfoContent);
    useEffect(() => {
        const scrollToForm = () => {
            const element = window.innerWidth > MD_WIDTH
                ? document.querySelector('.product-image-wrap')
                : document.querySelector('.product-config');
            gsapWithCSS.to(window, {
                duration: 0.5,
                scrollTo: window.scrollY + element.getBoundingClientRect().top,
                ease: 'sine.inOut',
                onComplete: () => {
                    setFocus(Object.keys(errors)[0]);
                },
            });
        };
        if (Object.keys(errors).length > 0) {
            scrollToForm();
        }
    }, [errors]);
    // Function to check if scent details should be shown
    const showScentDetails = (key) => scentKeys.includes(key);
    // Function to normalize scent keys for consistent matching
    const normalizeScentKey = (key) => key
        .toLowerCase()
        .normalize('NFD') // Decompose characters with diacritics
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
        .replace(/[^a-z0-9]+/g, '_') // Replace spaces and special chars with underscore
        .trim();
    // Function to set the current scent based on the selected option
    const setCurrentScentFromOption = (scentKey, key) => {
        if (key && showScentDetails(key)) {
            const normalizedKey = normalizeScentKey(scentKey);
            // Clear any existing timeout first
            if (transitionTimeoutRef.current) {
                clearTimeout(transitionTimeoutRef.current);
                transitionTimeoutRef.current = null;
            }
            // If we're going to show a new scent, do it immediately
            if (normalizedKey !== '') {
                setIsTransitioning(false);
                setCurrentScent(normalizedKey);
            }
            else {
                // Only set transitioning when clearing the scent
                setIsTransitioning(true);
                transitionTimeoutRef.current = setTimeout(() => {
                    setCurrentScent('');
                    setIsTransitioning(false);
                }, 250);
            }
        }
    };
    // Cleanup timeout on unmount
    useEffect(() => () => {
        if (transitionTimeoutRef.current) {
            clearTimeout(transitionTimeoutRef.current);
        }
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement("form", { key: `form-${router.asPath}`, onSubmit: handleSubmit(onSubmit), className: classnames({ 'has-info-variant': hasVariantInfoContent }), autoComplete: "off" },
            React.createElement("fieldset", null,
                React.createElement("legend", { className: "sr-only" }, tLabel('chooseOptions')),
                !oneOptionWithOneValue &&
                    options.map((option, index, list) => (React.createElement("div", { className: classnames('product-config-row', 'product-config-row-selector', index + 1 === list.length && 'product-config-row-selector-last'), key: option.option.attribute_code },
                        (() => {
                            if (option.fieldType === FieldType.Color) {
                                return (React.createElement(ColorField, { option: option, control: control, errors: errors, onColorChange: onColorChange, onOptionsChange: onOptionsChange, pageReady: pageReady, setValue: setValue }));
                            }
                            if (showScentDetails(option.option.attribute_code)) {
                                return (React.createElement(CardField, { option: option, control: control, errors: errors, onOptionsChange: onOptionsChange, setCurrentScent: setCurrentScentFromOption }));
                            }
                            return (React.createElement(SizeField, { ref: infoVariantButtonRef, option: option, register: register, errors: errors, onChartOpen: () => handleClickAttributeInfo(option), onOptionsChange: onOptionsChange, textSizeGuideButton: textSizeGuideButton }));
                        })(),
                        !alignCounterWithCartBtn && index + 1 === list.length && (React.createElement("div", { className: "col-right" },
                            React.createElement(Counter, { value: counterValue, setValue: setCounterValue, maxCount: maxQty, disabled: loading, counterButtons: counterButtons }))),
                        showScentDetails(option.option.attribute_code) && (React.createElement("div", { className: "scents-wrap clearfix" },
                            React.createElement("div", { className: "scent-details clear" },
                                React.createElement("p", { className: classnames('scent-details-text', { show: !currentScent }) }, t('$pages.$product.scentPreselectedDescription', {
                                    defaultValue: t('$common.$validation.selectOption'),
                                })),
                                currentScent && (React.createElement("div", { className: classnames('scent-info', { exiting: isTransitioning }) }, Object.entries(scents).map(([key, data]) => {
                                    if (normalizeScentKey(key) === currentScent) {
                                        return (React.createElement(React.Fragment, { key: key },
                                            React.createElement("div", { className: "scent-sprite", style: { backgroundPosition: data.sprite } }),
                                            React.createElement("p", { className: "scent-description" }, t(data.description))));
                                    }
                                    return null;
                                }))))))))),
                productStockMessage && (React.createElement("p", { role: "alert", className: "info-ui", id: "stockInfoMessage" }, productStockMessage)),
                React.createElement("div", { className: "product-wrap-addcart" },
                    !!relatedProducts.length && (React.createElement(RelatedProducts, { items: relatedProducts, parentProductType: product.type_id, checkedAddon: checkedAddon, quantityCounter: addonCounterValue, selectedProductVariant: selectedProductVariant, selectedRelatedProductsSku: selectedRelatedProductsSku, onRelatedProductClick: onRelatedProductClick })),
                    !alignCounterWithCartBtn && (React.createElement(Button, { buttonRef: submitRef, type: "submit", className: "product-add-to-bag base-btn bg-red", isLoading: loading, loadingMsg: t('$common.$labels.loading'), disabled: isFormFilled
                            ? !stockInfo.availableForSale || stockInfo.qty === 0
                            : !isProductAvailableForSale }, t('$common.$buttons.addToCart'))),
                    alignCounterWithCartBtn && (React.createElement("div", { className: "product-wrap-addcart-btn-and-qty" },
                        React.createElement(Counter, { value: counterValue, setValue: setCounterValue, maxCount: maxQty, disabled: loading, counterButtons: counterButtons }),
                        React.createElement(Button, { buttonRef: submitRef, type: "submit", className: "product-add-to-bag base-btn bg-red", isLoading: loading, loadingMsg: t('$common.$labels.loading'), disabled: isFormFilled
                                ? !stockInfo.availableForSale || stockInfo.qty === 0
                                : !isProductAvailableForSale }, t('$common.$buttons.addToCart')))),
                    React.createElement(AlmaBanner, { prices: prices })))),
        variantInfoContent && (React.createElement(InfoVariantModal, { content: variantInfoContent, sizeChartData: product.size_chart_data, onClose: onInfoVariantModalClose, sku: product.sku }))));
};

export { OptionsForm as O };
//# sourceMappingURL=OptionsForm-5RuRl0f9.js.map
